import React, { useState, useRef, useMemo } from 'react';
import { hoofdmenusVoor, hoofdmenuStartPad, isOpleiderRol } from './opleiderCockpit/opleiderMenu';
import { KlachtenreglementFooterLink } from './klachten/KlachtenreglementModal';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/authStore';
import { useLogout } from '../hooks/useAuth';
import { Button } from '../components';
import { useQuery } from '@tanstack/react-query';
import { chatAPI } from '../services/chatService';
import { LanguageSwitcher } from './LanguageSwitcher';
import { PlatformZoeken } from './zoeken/PlatformZoeken';
import { ActivatieWelkomScherm } from './ActivatieWelkomScherm';
import {
  Home,
  Briefcase,
  User,
  LogOut,
  Menu,
  X,
  ClipboardCheck,
  Users,
  DollarSign,
  Clock,
  Search,
  MessageSquare,
  ShieldCheck,
  ChevronDown,
  BookOpen,
  Bell,
  FolderOpen,
  FileText,
  Calendar,
  Flag,
  Settings,
  BarChart3,
  GraduationCap,
  Phone,
  Scale,
} from 'lucide-react';
import { PageBanner } from './PageBanner';
import { PaginaBannerContext } from './paginaBanner';
import { ProfessionalWerkSchil, professionalSchilVoorPad } from './ProfessionalWerkSchil';
import { AdminSchil } from './AdminSchil';
import { EmployerCockpitLayout } from './EmployerCockpitLayout';

interface LayoutProps {
  children: React.ReactNode;
}

interface NavSubItem {
  name: string;
  href: string;
  description?: string;
  disabled?: boolean;
  unreadCount?: number;
}

interface NavItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  submenu?: NavSubItem[];
}

// Statisch: buiten de component, zodat useMemo(bannerConfig) er niet van afhangt.
type BannerConfig = {
  label: string;
  title: string;
  subtitle?: string;
  // LucideIcon typeert `size` als string | number; een smallere signatuur
  // hier laat elke regel in de map afketsen op een propTypes-mismatch.
  icon: React.ComponentType<{ size?: string | number; color?: string; className?: string }>;
  sidebarLine?: boolean;
  titleRight?: boolean;
  sidebarWidthClass?: string;
};

const ROUTE_BANNERS: Array<[string, BannerConfig]> = [
  // Admin
  ['/admin/screenings', { label: 'Admin', title: 'Screenings', subtitle: 'Beoordeel en keur screenings goed of af', icon: ShieldCheck, sidebarLine: true, titleRight: true }],
  ['/admin/search-requests', { label: 'Admin', title: 'Zoekopdrachten', subtitle: 'Beheer alle zoekopdrachten van opdrachtgevers', icon: Search, sidebarLine: true, titleRight: true }],
  ['/admin/invoices', { label: 'Admin', title: 'Facturen', icon: DollarSign, sidebarLine: true, titleRight: true }],
  ['/admin/profile', { label: 'Admin', title: 'Instellingen', subtitle: 'Beheer platforminstellingen voor SEPA uitbetalingen', icon: ShieldCheck, sidebarLine: true, titleRight: true }],
  ['/admin/klachten', { label: 'Admin', title: 'Platformklachten', subtitle: 'Formele klachten volgens het klachtenreglement — reactie binnen 5 werkdagen', icon: Scale, sidebarLine: true, titleRight: true }],
  ['/admin', { label: 'Admin', title: 'Dashboard', subtitle: 'Beheer alle gebruikers en opdrachten op het platform', icon: Home, sidebarLine: true, titleRight: true }],
  // Professional
  ['/professional/screening', { label: 'Screening', title: 'Status en voortgang screening', icon: ShieldCheck, sidebarLine: true, titleRight: true }],
  ['/professional/cockpit', { label: 'Professioneel', title: 'Mijn Cockpit', subtitle: 'Ontdek nieuwe kansen en beheer je projecten', icon: Briefcase, sidebarLine: true, titleRight: true }],
  ['/professional/meldingen', { label: 'Professioneel', title: 'Meldingen', icon: Bell, sidebarLine: true, titleRight: true }],
  ['/professional/agenda', { label: 'Professioneel', title: 'Mijn Agenda', subtitle: 'Zet tijdslots open en handel afspraken af', icon: Calendar, sidebarLine: true, titleRight: true }],
  ['/professional/dossiers', { label: 'Professioneel', title: 'Mijn Dossiers', icon: FolderOpen, sidebarLine: true, titleRight: true }],
  ['/professional/documents', { label: 'Professioneel', title: 'Documenten', icon: FileText, sidebarLine: true, titleRight: true }],
  ['/professional/connections', { label: 'Professioneel', title: 'Mijn Netwerk', icon: Users, sidebarLine: true, titleRight: true }],
  ['/professional/invoices', { label: 'Professioneel', title: 'Mijn Facturen', icon: DollarSign, sidebarLine: true, titleRight: true }],
  ['/professional/hours', { label: 'Professioneel', title: 'Mijn Uren', icon: Clock, sidebarLine: true, titleRight: true }],
  ['/professional/projecten', { label: 'Professioneel', title: 'Projecten & Mijlpalen', subtitle: 'Schrijf uren op vooraf goedgekeurde mijlpalen', icon: Flag, sidebarLine: true, titleRight: true }],
  // Employer routes all use EmployerCockpitLayout (excluded from bannerConfig above)
  // Opleider routes use OpleiderCockpitLayout (excluded from bannerConfig above)
  // Community
  ['/intervisie', { label: 'Community', title: 'Intervisie', subtitle: 'Reflecteer en leer samen met collega-professionals', icon: Users, sidebarLine: true, titleRight: true }],
  ['/newsroom', { label: 'Community', title: 'Newsroom', subtitle: 'Nieuws en artikelen uit het vakgebied', icon: FileText, sidebarLine: true, titleRight: true }],
  ['/connect-meet', { label: 'Community', title: 'Connect & Meet', subtitle: 'Ontmoet collega-professionals en wissel ervaringen uit', icon: Calendar, sidebarLine: true, titleRight: true }],
  ['/professionals/chat', { label: 'Community', title: 'Chat met Professionals', icon: MessageSquare, sidebarLine: true, titleRight: true }],
  ['/community/professionals', { label: 'Community', title: 'Professionals', subtitle: 'Het smoelenboek van BeLagom-vakgenoten', icon: Users, sidebarLine: true, titleRight: true }],
  ['/community', { label: 'Community', title: 'Communitie forum', subtitle: 'Deel kennis en verbind met vakgenoten', icon: Users, sidebarLine: true, titleRight: true }],
  // Overige authenticated
  ['/berichten', { label: 'Belagom', title: 'Mijn Berichten', icon: MessageSquare, sidebarLine: true, titleRight: true }],
  ['/opleidingen', { label: 'Platform', title: 'Opleidingen & Workshops', subtitle: 'Exclusief geprijsd — minimaal 5% voordeliger dan rechtstreeks bij de aanbieder.', icon: BookOpen, sidebarLine: true, titleRight: true }],
  ['/vacatures', { label: 'Platform', title: 'Vacatures', icon: Briefcase, sidebarLine: true, titleRight: true }],
  ['/opdrachten', { label: 'Platform', title: 'Opdrachten', icon: Briefcase, sidebarLine: true, titleRight: true }],
  ['/profiel', { label: 'Account', title: 'Mijn Profiel', icon: User, sidebarLine: true, titleRight: true }],
];

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);
  const submenuTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { user, isAuthenticated, isImpersonating } = useAuthStore();
  const magToegangsbeheer =
    user?.role === 'opdrachtgever' ||
    user?.employer_toegangsniveau === 'global_admin' ||
    user?.employer_toegangsniveau === 'vestigingsmanager';
  const navigate = useNavigate();
  const location = useLocation();
  const logoutMutation = useLogout();
  const { t } = useTranslation();

  // Get unread message count
  const { data: conversations } = useQuery({
    queryKey: ['conversations'],
    queryFn: async () => {
      if (!isAuthenticated) return [];
      return await chatAPI.getConversations();
    },
    enabled: isAuthenticated && (user?.role === 'professional' || user?.role === 'opdrachtgever'),
    refetchInterval: 60000, // Refetch every minute
  });

  const totalUnreadCount = conversations?.reduce((sum, conv) => sum + (conv.unread_count || 0), 0) || 0;

  const belagomUnread = conversations?.filter(c => c.type === 'admin_broadcast' || c.type === 'professional_belagom').reduce((sum, c) => sum + (c.unread_count || 0), 0) || 0;
  const opdrachtgeverUnread = conversations?.filter(c => c.type === 'professional_employer').reduce((sum, c) => sum + (c.unread_count || 0), 0) || 0;
  const professionalsUnread = conversations?.filter(c => c.type === 'professional_peer' || c.type === 'professional_group').reduce((sum, c) => sum + (c.unread_count || 0), 0) || 0;


  const handleLogout = () => {
    const wasImpersonating = isImpersonating;

    logoutMutation.mutate(undefined, {
      onSuccess: () => {
        // Small delay to ensure auth store state is fully updated
        setTimeout(() => {
          const finalAuthState = useAuthStore.getState();

          if (wasImpersonating && finalAuthState.isAuthenticated && finalAuthState.user?.role === 'admin') {
            // We returned to admin session, go to admin dashboard
            navigate('/admin');
          } else {
            // Regular logout, go to home page
            navigate('/');
          }
        }, 100);
      },
      onError: () => {
        // If logout fails, still handle navigation
        if (wasImpersonating) {
          const finalAuthState = useAuthStore.getState();
          if (finalAuthState.isAuthenticated && finalAuthState.user?.role === 'admin') {
            navigate('/admin');
          } else {
            navigate('/');
          }
        } else {
          navigate('/');
        }
      }
    });
  };

  // Professional Academy (Netflix-stijl) draait in "theatermodus": licht (huisstijl), breed, geen banner.
  const theater =
    location.pathname === '/opleidingen' || location.pathname.startsWith('/opleidingen/opleider/');

  const bannerConfig = useMemo<BannerConfig | null>(() => {
    if (theater) return null;
    const isEmployer = user?.role === 'opdrachtgever' || user?.role === 'employer_member';
    if (
      location.pathname === '/professional/dashboard' ||
      location.pathname.startsWith('/professional/cockpit') ||
      // Mijn Belagom: titel staat in de inhoudskolom naast het ZijMenu
      location.pathname === '/professional/belagom' ||
      location.pathname.startsWith('/employer/') ||
      location.pathname.startsWith('/opleider/') ||
      (isEmployer && location.pathname === '/berichten')
    ) return null;

    return ROUTE_BANNERS.find(([route]) => location.pathname.startsWith(route))?.[1] ?? null;
  }, [location.pathname, location.search, user?.role, theater]);

  // Zijmenu-schillen (huisstijl okt 2026): pagina's zonder eigen linkerkolom krijgen
  // het menu van hun rol; de bannertitel staat dan als PaginaKop in de inhoud.
  const isEmployerRol = user?.role === 'opdrachtgever' || user?.role === 'employer_member';
  const professionalSchil = isAuthenticated && user?.role === 'professional' && !theater && !!professionalSchilVoorPad(location.pathname);
  const adminSchil = isAuthenticated && user?.role === 'admin' && location.pathname.startsWith('/admin');
  const employerSchil =
    isAuthenticated &&
    isEmployerRol &&
    ['/employer/search-results', '/employer/vacancies', '/employer/dossiers', '/employer/professional/'].some((pad) => location.pathname.startsWith(pad));
  // Pagina's die zelf CommunityLayout (met ZijMenu) gebruiken
  const communityInhoud = ['/community', '/community/professionals', '/newsroom', '/intervisie', '/connect-meet'].includes(location.pathname);
  const toonBanner = !!bannerConfig && !professionalSchil && !adminSchil && !communityInhoud;

  // Website-only routes where we don't want the full app navigation (e.g. registration & screening wizard)
  const isMinimalLayoutRoute =
    location.pathname === '/register' ||
    location.pathname === '/login' ||
    location.pathname === '/backup' ||
    (!isAuthenticated && location.pathname.startsWith('/professional/screening')) ||
    (!isAuthenticated && location.pathname.startsWith('/employer/screening'));

  const navigation: NavItem[] = isAuthenticated
    ? user?.role === 'admin'
      ? [
          { name: t('nav.dashboard'), href: '/admin', icon: Home },
          { name: t('nav.userManagement'), href: '/admin?view=users', icon: Users },
          { name: t('nav.screenings'), href: '/admin/screenings', icon: ShieldCheck },
          { name: t('nav.searchRequests'), href: '/admin/search-requests', icon: Search },
          { name: t('nav.invoices'), href: '/admin/invoices', icon: DollarSign },
          { name: t('nav.profile'), href: '/profiel', icon: User },
        ]
      : user?.role === 'opdrachtgever' || user?.role === 'employer_member'
      ? [
          // Hoofdmenu opdrachtgever: Dashboard | Mijn Cockpit | Trajecten | Data | Academy | Contact
          {
            name: t('nav.dashboard'),
            href: '/employer/dashboard/actiepunten',
            icon: Home,
            // Exact vier banen (zelfde volgorde als SIDEBAR_ITEMS in EmployerCockpitSidebar)
            submenu: [
              { name: t('nav.sub.actiepunten'), href: '/employer/dashboard/actiepunten' },
              { name: t('nav.sub.alleSignalen'), href: '/employer/dashboard/signalen' },
              { name: t('employerSidebar.byop'), href: '/employer/byop' },
              { name: t('employerSidebar.professionalZoeken'), href: '/employer/dashboard/zoeken' },
            ],
          },
          {
            name: t('nav.mijnCockpit'),
            href: '/employer/mijn-belagom',
            icon: Settings,
            submenu: [
              { name: t('nav.sub.organisatieprofiel'), href: '/employer/mijn-belagom?tab=profiel' },
              { name: t('nav.sub.contracten'), href: '/employer/mijn-belagom?tab=contracten' },
              // Toegangsbeheer: verborgen voor afdelingschefs (backend weigert hen ook)
              ...(magToegangsbeheer ? [{ name: t('nav.sub.toegangsbeheer'), href: '/employer/mijn-belagom?tab=toegang' }] : []),
              { name: t('nav.sub.poolConfigurator'), href: '/employer/mijn-belagom?tab=pools' },
              { name: t('nav.sub.werkgebied'), href: '/employer/mijn-belagom?tab=werkgebied' },
              { name: t('nav.sub.statusOnderhandelingen'), href: '/employer/mijn-belagom?tab=onderhandelingen' },
              { name: t('nav.sub.medewerkersportaal'), href: '/employer/mijn-belagom?tab=portaal' },
              { name: t('nav.sub.flexbudget'), href: '/employer/mijn-belagom?tab=flexbudget' },
              { name: t('nav.sub.crisisdienst'), href: '/employer/mijn-belagom?tab=crisis' },
              { name: t('nav.sub.vertrouwenspersoon'), href: '/employer/mijn-belagom?tab=vertrouwenspersoon' },
              ...(magToegangsbeheer ? [{ name: t('nav.sub.gebruikers'), href: '/employer/team' }] : []),
            ],
          },
          {
            name: t('nav.trajecten'),
            href: '/employer/trajecten/agenda',
            icon: FolderOpen,
            submenu: [
              { name: t('nav.sub.trajectenAgenda'), href: '/employer/trajecten/agenda' },
              { name: t('nav.sub.trajectenLopend'), href: '/employer/trajecten/lopend' },
              { name: t('nav.sub.gecontracteerdeProfessionals'), href: '/employer/trajecten/professionals' },
              { name: t('nav.sub.mijlpalen'), href: '/employer/trajecten/mijlpalen' },
              { name: t('nav.sub.facturen'), href: '/employer/trajecten/facturen' },
              { name: t('nav.sub.gespreksverslagen'), href: '/employer/trajecten/gespreksverslagen' },
              { name: t('nav.sub.jaarverslagen'), href: '/employer/trajecten/jaarverslagen' },
            ],
          },
          {
            name: t('nav.data'),
            href: '/employer/data/verzuim',
            icon: BarChart3,
            // Menu 4: exact vijf vaste submappen (zelfde volgorde als DATA_ITEMS in EmployerCockpitSidebar)
            submenu: [
              { name: t('nav.sub.dataVerzuim'), href: '/employer/data/verzuim' },
              { name: t('nav.sub.dataCrisis'), href: '/employer/data/crisis' },
              { name: t('nav.sub.dataBudgetten'), href: '/employer/data/budgetten' },
              { name: t('nav.sub.dataCultuur'), href: '/employer/data/cultuur' },
              { name: t('nav.sub.dataPool'), href: '/employer/data/pool' },
            ],
          },
          {
            name: t('nav.academy'),
            href: '/employer/opleidingen',
            icon: GraduationCap,
            submenu: [
              { name: t('nav.sub.opleidingenWorkshops'), href: '/employer/opleidingen' },
              { name: t('nav.sub.academyMedewerkers'), href: '/employer/medewerkersportaal?tab=academy' },
            ],
          },
          {
            name: t('nav.contact'),
            href: '/berichten',
            icon: Phone,
            submenu: [
              { name: t('nav.sub.chatProfessioneel'), href: '/berichten?tab=opdrachtgever', unreadCount: opdrachtgeverUnread },
              { name: t('nav.sub.chatBelagom'), href: '/berichten?tab=belagom', unreadCount: belagomUnread },
              { name: t('nav.sub.alleBerichten'), href: '/berichten' },
            ],
          },
        ]
      : user?.role === 'opleider'
      ? (() => {
          // Opleiders-cockpit: 5 hoofdmenu's met submenu's, gefilterd op teamrol (opleiderMenu.ts).
          // Zonder bekende rol (nog geen /auth/me) alleen het minimale TRAINER-menu tonen.
          const rol = isOpleiderRol(user?.opleider_rol) ? user.opleider_rol : 'TRAINER';
          const menus: NavItem[] = hoofdmenusVoor(rol).map((m) => ({
            name: t(`opleiderNav.${m.key}`),
            href: hoofdmenuStartPad(m, rol),
            icon: m.icon,
            submenu: m.items.map((i) => ({ name: t(`opleiderNav.sub.${i.key}`), href: i.pad })),
          }));
          return [...menus, { name: t('nav.profile'), href: '/profiel', icon: User }];
        })()
      : (() => {
          // Hoofdmenu professional (spiegel van de opdrachtgever, plus Community):
          // Dashboard | Mijn Cockpit | Trajecten | Data | Academie | Community | Contact.
          // 'Opdrachten' is geen los hoofditem meer maar zit onder Dashboard.
          return [
            {
              name: t('nav.dashboard'),
              href: '/professional/dashboard',
              icon: Home,
              submenu: [
                { name: t('nav.sub.actiepunten'), href: '/professional/dashboard/actiepunten' },
                { name: t('nav.sub.alleSignalen'), href: '/professional/dashboard/signalen' },
                { name: t('nav.sub.opdrachten'), href: '/professional/dashboard/opdrachten' },
              ],
            },
            {
              name: t('nav.mijnCockpit'),
              href: '/professional/belagom',
              icon: Settings,
              submenu: [
                { name: t('nav.sub.screening'), href: '/professional/belagom?view=screening' },
                { name: t('nav.sub.uitbetalingen'), href: '/professional/belagom?view=uitbetalingen' },
                { name: t('nav.sub.portfolio'), href: '/professional/belagom?view=portfolio' },
                { name: t('nav.sub.poolsTarieven'), href: '/professional/belagom?view=pools' },
                { name: t('nav.sub.contracten'), href: '/professional/belagom?view=contracten' },
                { name: t('nav.sub.byoe'), href: '/professional/byoe' },
                { name: t('nav.sub.crisisStandby'), href: '/professional/belagom?view=crisis' },
                { name: t('nav.sub.waakvlam'), href: '/professional/belagom?view=vertrouwenspersoon' },
              ],
            },
            {
              name: t('nav.trajecten'),
              href: '/professional/cockpit',
              icon: FolderOpen,
              submenu: [
                { name: t('nav.sub.trajectenAgenda'), href: '/professional/cockpit?view=agenda' },
                { name: t('nav.sub.trajectenLopend'), href: '/professional/cockpit?view=lopende_trajecten' },
                { name: t('nav.sub.opdrachtgeversPools'), href: '/professional/cockpit?view=opdrachtgevers' },
                { name: t('nav.sub.financien'), href: '/professional/cockpit?view=financien' },
                { name: t('nav.sub.reviews'), href: '/professional/cockpit?view=reviews' },
                { name: t('nav.sub.projecten'), href: '/professional/projecten' },
                { name: t('nav.sub.projectvoorstellen'), href: '/professional/projectvoorstellen' },
              ],
            },
            {
              name: t('nav.data'),
              href: '/professional/data/beroepsregistratie',
              icon: BarChart3,
              // Data (Menu 4): vast, genummerd submenu 1–5 (ProfessionalDataPage) + de jaarverslag-werkpagina
              submenu: [
                { name: `1. ${t('nav.sub.dataBeroepsregistratie')}`, href: '/professional/data/beroepsregistratie' },
                { name: `2. ${t('nav.sub.dataBedrijfscijfers')}`, href: '/professional/data/bedrijfscijfers' },
                { name: `3. ${t('nav.sub.dataMeldingsmonitor')}`, href: '/professional/data/meldingsmonitor' },
                { name: `4. ${t('nav.sub.dataKwaliteit')}`, href: '/professional/data/kwaliteit' },
                { name: `5. ${t('nav.sub.dataMarkt')}`, href: '/professional/data/markt' },
                { name: t('nav.sub.caseRegistratie'), href: '/professional/jaarverslag' },
              ],
            },
            { name: t('nav.academie'), href: '/opleidingen', icon: GraduationCap },
            {
              name: t('nav.community'),
              href: '/community/professionals',
              icon: Users,
              submenu: [
                { name: t('nav.sub.professionals'), href: '/community/professionals', description: t('nav.sub.professionalsDesc') },
                { name: t('nav.sub.chatProfessionals'), href: '/professionals/chat', description: t('nav.sub.chatProfessionalsDesc') },
                { name: t('nav.sub.intervisie'), href: '/intervisie', description: t('nav.sub.intervisieDesc') },
                { name: t('nav.sub.forum'), href: '/community', description: t('nav.sub.forumDesc') },
                { name: t('nav.sub.news'), href: '/newsroom', description: t('nav.sub.newsDesc') },
                { name: t('nav.sub.meetConnect'), href: '/connect-meet', description: t('nav.sub.meetConnectDesc') },
              ],
            },
            {
              name: t('nav.contact'),
              href: '/professional/contact',
              icon: Phone,
              submenu: [
                { name: t('nav.sub.contactHub'), href: '/professional/contact' },
                { name: t('nav.sub.chatBelagom'), href: '/berichten?tab=belagom', unreadCount: belagomUnread },
                { name: t('nav.sub.chatEmployer'), href: '/berichten?tab=opdrachtgever', unreadCount: opdrachtgeverUnread },
                { name: t('nav.sub.chatWithProfessionals'), href: '/berichten?tab=professionals', unreadCount: professionalsUnread },
                { name: t('nav.sub.chatTrainers'), href: '/berichten?tab=opleiders', unreadCount: 0 },
              ],
            },
          ];
        })()
    : [
        { name: t('nav.home'), href: '/', icon: Home },
        { name: t('nav.approach'), href: '/werkwijze', icon: ClipboardCheck },
        { name: t('nav.about'), href: '/over', icon: User },
        { name: t('nav.forProfessionals'), href: '/voor-professionals', icon: Briefcase },
        { name: t('nav.forPartners'), href: '/voor-partners', icon: Briefcase },
      ];

  // Actieve hoofdmenu-tab: submenu-links met ?query (bv. /employer/cockpit?view=reviews onder Data)
  // winnen van een hoofditem dat alleen op pathname matcht (Dashboard = /employer/cockpit).
  const isNavItemActive = (item: NavItem): boolean => {
    const fullLocation = location.pathname + location.search;
    const subMatches = (sub: NavSubItem) =>
      sub.href.includes('?')
        ? fullLocation === sub.href
        : location.pathname === sub.href ||
          (user?.role === 'opleider' && (location.pathname.startsWith(sub.href + '/') || (sub.href === '/opleider/academy/cursus-invoeren' && location.pathname.startsWith('/opleider/trainings'))));
    if (item.submenu?.some(subMatches)) return true;
    const claimedByOther = navigation.some(
      (other) => other !== item && other.submenu?.some((sub) => sub.href.includes('?') && fullLocation === sub.href)
    );
    if (claimedByOther) return false;
    return item.href.includes('?') ? fullLocation === item.href : location.pathname === item.href;
  };

  // Medewerkersportaal heeft een eigen schil (MedewerkerLayout); geen platform-navigatie voor pseudoniem-accounts.
  if (isAuthenticated && user?.role === 'medewerker' && location.pathname.startsWith('/medewerker')) {
    return <>{children}</>;
  }

  // Opleider-onboarding: full-screen wizard, geen menu's tot ONBOARDED_ACTIVE (Rechten-Grendel).
  if (location.pathname === '/opleider/onboarding' || location.pathname === '/opleider/register') {
    return <>{children}</>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Minimal layout for registration/screening: no top navigation, only page content */}
      {isMinimalLayoutRoute ? (
        <>
          <main className="flex-1">
            {children}
          </main>
          <footer className="relative bg-white border-t border-gray-200 overflow-hidden">
            <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
              <div className="text-center text-sm text-gray-500">
                {t('footer.copyright', { year: new Date().getFullYear() })}
              </div>
            </div>
            <img
              src="/belagom-beeldmerk.png"
              alt=""
              aria-hidden="true"
              className="absolute bottom-0 right-0 translate-x-[35%] translate-y-[35%] h-32 w-auto opacity-20 pointer-events-none select-none"
            />
          </footer>
        </>
      ) : (
        <>
          {/* Navigation (data-zoek-negeer: menu-items staan al als pagina's in het zoekvenster) */}
          <nav data-zoek-negeer className="bg-navy-900 shadow-sm">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex justify-between h-16">
                <div className="flex">
                  {/* Logo */}
                  <Link to="/" className="flex items-center gap-2">
                    <img src="/belagom-logo-light.svg" alt="BeLagom" className="h-8 w-auto max-w-none flex-shrink-0" />
                  </Link>

                  {/* Desktop navigation */}
                  <div className="hidden sm:ml-6 sm:flex sm:space-x-4 xl:space-x-6 sm:mt-4">
                    {navigation.map((item) => {
                      const Icon = item.icon;
                      const isActive = isNavItemActive(item);
                      const hasSubmenu = item.submenu && item.submenu.length > 0;

                      return (
                        <div
                          key={item.name}
                          className="relative group"
                          onMouseEnter={() => {
                            if (hasSubmenu) {
                              if (submenuTimeout.current) { clearTimeout(submenuTimeout.current); submenuTimeout.current = null; }
                              setOpenSubmenu(item.name);
                            }
                          }}
                          onMouseLeave={() => {
                            if (hasSubmenu) {
                              submenuTimeout.current = setTimeout(() => setOpenSubmenu(null), 150);
                            }
                          }}
                        >
                          {hasSubmenu ? (
                            <button
                              type="button"
                              onClick={() => setOpenSubmenu(openSubmenu === item.name ? null : item.name)}
                              className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium whitespace-nowrap transition-colors ${
                                isActive
                                  ? 'border-gold-500 text-white'
                                  : 'border-transparent text-white/75 hover:border-gold-500/60 hover:text-white'
                              }`}
                            >
                              <Icon className="w-4 h-4 mr-2 text-gold-400" />
                              {item.name}
                              {(item.name === t('nav.messages') || item.name === t('nav.contact')) && totalUnreadCount > 0 && (
                                <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-4 px-1 text-xs font-bold text-white bg-red-500 rounded-full">
                                  {totalUnreadCount > 99 ? '99+' : totalUnreadCount}
                                </span>
                              )}
                              <ChevronDown className={`w-3 h-3 ml-1 transition-transform ${openSubmenu === item.name ? 'rotate-180' : ''}`} />
                            </button>
                          ) : (
                            <Link
                              to={item.href}
                              className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium whitespace-nowrap transition-colors ${
                                isActive
                                  ? 'border-gold-500 text-white'
                                  : 'border-transparent text-white/75 hover:border-gold-500/60 hover:text-white'
                              }`}
                            >
                              <div className="relative">
                                <Icon className="w-4 h-4 mr-2 text-gold-400" />
                              </div>
                              {item.name}
                              {(item.name === t('nav.messages') || item.name === t('nav.contact')) && totalUnreadCount > 0 && (
                                <span className="ml-2 inline-flex items-center justify-center min-w-[18px] h-4 px-1 text-xs font-bold text-white bg-red-500 rounded-full">
                                  {totalUnreadCount > 99 ? '99+' : totalUnreadCount}
                                </span>
                              )}
                            </Link>
                          )}

                          {/* Submenu dropdown */}
                          {hasSubmenu && openSubmenu === item.name && (
                            <div
                              className="nav-sidebar-submenu absolute left-0 top-full pt-2 z-50"
                              style={{ minWidth: '280px' }}
                              onMouseEnter={() => {
                                if (submenuTimeout.current) { clearTimeout(submenuTimeout.current); submenuTimeout.current = null; }
                                setOpenSubmenu(item.name);
                              }}
                              onMouseLeave={() => {
                                submenuTimeout.current = setTimeout(() => setOpenSubmenu(null), 150);
                              }}
                            >
                            <div className="bg-white rounded-lg shadow-lg py-2 border border-gray-200">
                              {item.submenu?.map((subItem, idx) => {
                                const [subPath, subQuery] = subItem.href.split('?');
                                const isSubActive = subQuery
                                  ? location.pathname === subPath && location.search === `?${subQuery}`
                                  : location.pathname === subItem.href;
                                const isDisabled = subItem.disabled;
                                const showDivider = idx > 0 && item.submenu?.[idx - 1]?.disabled !== subItem.disabled;
                                return (
                                  <div key={subItem.name}>
                                    {showDivider && <div className="border-t border-gray-100 my-1" />}
                                    {isDisabled ? (
                                      <div className="flex items-center justify-between px-4 py-2.5 cursor-not-allowed opacity-60">
                                        <div>
                                          <div className="text-sm font-medium text-gray-500">{subItem.name}</div>
                                          {subItem.description && (
                                            <div className="text-xs text-gray-400 mt-0.5">{subItem.description}</div>
                                          )}
                                        </div>
                                        <span className="text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded ml-2 flex-shrink-0">
                                          {t('common.comingSoon')}
                                        </span>
                                      </div>
                                    ) : (
                                      <Link
                                        to={subItem.href}
                                        onClick={() => setOpenSubmenu(null)}
                                        className={`flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 transition-colors ${
                                          isSubActive ? 'bg-gold-50' : ''
                                        }`}
                                      >
                                        <div>
                                          <div className={`text-sm font-medium ${isSubActive ? 'text-[#0B1F3A]' : 'text-gray-800'}`}>
                                            {subItem.name}
                                          </div>
                                          {subItem.description && (
                                            <div className="text-xs text-gray-400 mt-0.5">{subItem.description}</div>
                                          )}
                                        </div>
                                        <div className="flex items-center gap-1.5 ml-2 flex-shrink-0">
                                          {(subItem.unreadCount ?? 0) > 0 && (
                                            <span className="inline-flex items-center justify-center min-w-[18px] h-4 px-1 text-xs font-bold text-white bg-red-500 rounded-full">
                                              {(subItem.unreadCount ?? 0) > 99 ? '99+' : subItem.unreadCount}
                                            </span>
                                          )}
                                          {isSubActive && (
                                            <div className="w-1.5 h-1.5 rounded-full bg-gold-500" />
                                          )}
                                        </div>
                                      </Link>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Desktop auth buttons + language switcher */}
                <div className="hidden sm:ml-6 sm:flex sm:items-center sm:space-x-4">
                  <LanguageSwitcher tone="dark" />
                  {isAuthenticated ? (
                    <div className="flex items-center space-x-4">
                      <div className="text-sm">
                        {isImpersonating ? (
                          <div className="text-gold-300 font-medium">
                            <span className="text-gold-400">👤 {t('auth.loggedInAs')}</span> {user?.first_name}
                          </div>
                        ) : (
                          <span className="block leading-tight whitespace-nowrap">
                            <span className="block text-[11px] text-white/60">{t('auth.loggedInAs')}</span>
                            <span className="block font-semibold text-white">{user?.first_name}</span>
                          </span>
                        )}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleLogout}
                        loading={logoutMutation.isPending}
                        title={isImpersonating ? t('auth.returnToAdmin') : t('auth.logout')}
                      >
                        <LogOut className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-4">
                      <Link to="/login">
                        <Button variant="outline" size="sm">
                          {t('auth.login')}
                        </Button>
                      </Link>
                      <Link to="/register">
                        <Button size="sm">
                          {t('auth.register')}
                        </Button>
                      </Link>
                    </div>
                  )}
                </div>

                {/* Mobile menu button */}
                <div className="sm:hidden flex items-center">
                  <button
                    type="button"
                    className="inline-flex items-center justify-center p-2 rounded-md text-white/80 hover:text-white hover:bg-white/10"
                    onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  >
                    {mobileMenuOpen ? (
                      <X className="block h-6 w-6" />
                    ) : (
                      <Menu className="block h-6 w-6" />
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Mobile menu */}
            {mobileMenuOpen && (
              <div className="sm:hidden bg-white">
                <div className="pt-2 pb-3 space-y-1">
                  {navigation.map((item) => {
                    const Icon = item.icon;
                    const isActive = isNavItemActive(item);
                    const hasSubmenu = item.submenu && item.submenu.length > 0;
                    const isSubmenuOpen = openSubmenu === item.name;

                    return (
                      <div key={item.name}>
                        <div
                          className={`flex items-center justify-between pl-3 pr-4 py-2 border-l-4 text-base font-medium ${
                            isActive
                              ? 'bg-gold-50 border-gold-500 text-navy-900'
                              : 'border-transparent text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800'
                          }`}
                        >
                          <Link
                            to={item.href}
                            className="flex items-center flex-1"
                            onClick={() => !hasSubmenu && setMobileMenuOpen(false)}
                          >
                            <div className="relative">
                              <Icon className="w-5 h-5 mr-3" />
                            </div>
                            {item.name}
                            {(item.name === t('nav.messages') || item.name === t('nav.contact')) && totalUnreadCount > 0 && (
                              <span className="ml-2 inline-flex items-center justify-center min-w-[18px] h-4 px-1 text-xs font-bold text-white bg-red-500 rounded-full">
                                {totalUnreadCount > 99 ? '99+' : totalUnreadCount}
                              </span>
                            )}
                          </Link>
                          {hasSubmenu && (
                            <button
                              onClick={() => setOpenSubmenu(isSubmenuOpen ? null : item.name)}
                              className="p-1"
                            >
                              <ChevronDown className={`w-4 h-4 transition-transform ${isSubmenuOpen ? 'rotate-180' : ''}`} />
                            </button>
                          )}
                        </div>
                        {hasSubmenu && isSubmenuOpen && (
                          <div className="pl-8 space-y-1 bg-gray-50">
                            {item.submenu?.map((subItem) => {
                              const [subPath, subQuery] = subItem.href.split('?');
                              const isSubActive = subQuery
                                ? location.pathname === subPath && location.search === `?${subQuery}`
                                : location.pathname === subItem.href;
                              if (subItem.disabled) {
                                return (
                                  <div key={subItem.name} className="flex items-center justify-between px-4 py-2 opacity-60">
                                    <span className="text-sm text-gray-500">{subItem.name}</span>
                                    <span className="text-xs bg-gray-200 text-gray-500 px-1.5 py-0.5 rounded">{t('common.comingSoon')}</span>
                                  </div>
                                );
                              }
                              return (
                                <Link
                                  key={subItem.name}
                                  to={subItem.href}
                                  className={`flex items-center justify-between px-4 py-2 text-sm ${
                                    isSubActive
                                      ? 'bg-blue-100 text-blue-700 font-medium'
                                      : 'text-gray-600 hover:bg-gray-100'
                                  }`}
                                  onClick={() => setMobileMenuOpen(false)}
                                >
                                  <span>{subItem.name}</span>
                                  {(subItem.unreadCount ?? 0) > 0 && (
                                    <span className="inline-flex items-center justify-center min-w-[18px] h-4 px-1 text-xs font-bold text-white bg-red-500 rounded-full">
                                      {(subItem.unreadCount ?? 0) > 99 ? '99+' : subItem.unreadCount}
                                    </span>
                                  )}
                                </Link>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className="pt-4 pb-3 border-t border-gray-200">
                  <div className="px-4 pb-3">
                    <LanguageSwitcher />
                  </div>
                  {isAuthenticated ? (
                    <div className="flex items-center px-4">
                      <div className="flex-shrink-0">
                        <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                          <User className="h-6 w-6 text-gray-600" />
                        </div>
                      </div>
                      <div className="ml-3 flex-1">
                        {isImpersonating ? (
                          <div>
                            <div className="text-base font-medium text-orange-600">
                              👤 {user?.first_name} {user?.last_name}
                            </div>
                            <div className="text-sm font-medium text-gray-500">
                              {user?.email}
                            </div>
                          </div>
                        ) : (
                          <div>
                            <div className="text-base font-medium text-gray-800">
                              {user?.first_name} {user?.last_name}
                            </div>
                            <div className="text-sm font-medium text-gray-500">
                              {user?.email}
                            </div>
                          </div>
                        )}
                      </div>
                      <button
                        onClick={handleLogout}
                        className="ml-auto flex-shrink-0 p-1 text-gray-400 hover:text-gray-500"
                        title={isImpersonating ? t('auth.returnToAdmin') : t('auth.logout')}
                      >
                        <LogOut className="h-6 w-6" />
                      </button>
                    </div>
                  ) : (
                    <div className="px-4 space-y-3">
                      <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                        <Button variant="outline" className="w-full">
                          {t('auth.login')}
                        </Button>
                      </Link>
                      <Link to="/register" onClick={() => setMobileMenuOpen(false)}>
                        <Button className="w-full">
                          {t('auth.register')}
                        </Button>
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            )}
          </nav>

          {/* Page banner */}
          {toonBanner && bannerConfig && (
            <PageBanner label={bannerConfig.label} title={bannerConfig.title} subtitle={bannerConfig.subtitle} icon={bannerConfig.icon as React.ComponentType<{ size?: number; color?: string; className?: string }>} sidebarLine={bannerConfig.sidebarLine} titleRight={bannerConfig.titleRight} sidebarWidthClass={bannerConfig.sidebarWidthClass} />
          )}

          {/* Main content */}
          <main className="flex-1">
            <PaginaBannerContext.Provider value={bannerConfig}>
              {theater ? (
                <div className="mx-auto w-full max-w-7xl px-4 py-6 pb-16 sm:px-6 lg:px-8">{children}</div>
              ) : professionalSchil ? (
                <ProfessionalWerkSchil titel={bannerConfig?.title} ondertitel={bannerConfig?.subtitle}>{children}</ProfessionalWerkSchil>
              ) : adminSchil ? (
                <AdminSchil titel={bannerConfig?.title} ondertitel={bannerConfig?.subtitle}>{children}</AdminSchil>
              ) : employerSchil ? (
                <EmployerCockpitLayout>{children}</EmployerCockpitLayout>
              ) : (
                children
              )}
            </PaginaBannerContext.Provider>
          </main>

          {/* Zwevende knoppen rechtsonder: zoeken (Ctrl K) voor iedereen; voor de professional
              ook de feedback- en klantenservice-widget, die het Mijn Belagom-menu beperkt
              houdt tot bedrijfsmatige compliance-onderdelen (Wet DBA). */}
          <PlatformZoeken
            navigation={navigation}
            paginaTitel={bannerConfig?.title}
            metSupport={isAuthenticated && user?.role === 'professional'}
            onUitloggen={isAuthenticated ? handleLogout : undefined}
          />

          {/* Eenmalig welkomst- & activatiescherm (contract getekend + Stripe geverifieerd).
              Niet tijdens impersonatie: dan zou de admin het voor de professional wegklikken. */}
          {isAuthenticated && user?.role === 'professional' && <ActivatieWelkomScherm enabled={!isImpersonating} />}


          {/* Footer */}
          <footer className="relative overflow-hidden border-t border-gray-200 bg-white">
            <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
              {/* Universele compliance-footer: klachtenreglement + doorlink naar het formulier (alle ingelogde omgevingen) */}
              {isAuthenticated && (
                <div className="mb-3 flex justify-center">
                  <KlachtenreglementFooterLink />
                </div>
              )}
              <div className="text-center text-sm text-gray-500">
                {t('footer.copyright', { year: new Date().getFullYear() })}
              </div>
            </div>
            <img
              src="/belagom-beeldmerk.png"
              alt=""
              aria-hidden="true"
              className="absolute bottom-0 right-0 translate-x-[35%] translate-y-[35%] h-32 w-auto opacity-20 pointer-events-none select-none"
            />
          </footer>
        </>
      )}
    </div>
  );
};
